import React from 'react';
import { Filter } from 'lucide-react';
import ConditionBuilder from './ConditionBuilder';
import { Condition, FilterConfig, RegistryEnabledModel, WorkflowStep } from '../../types';
import { apiService } from '../../services/api';

/**
 * Configuration form for the `filter` orchestration step type (AC-1273, AC-1279).
 *
 * Rendered inside ``StepConfigurationModal`` when ``stepType === 'filter'``.
 * Produces a ``FilterConfig`` stored under ``orchestration_step.config``.
 *
 * Layout:
 *   - mode: predicate vs registry_dedup (AC-1358).
 *   - source_field: dropdown populated from upstream output_model's list[X] fields.
 *   - output_field: text input, defaults to source_field.
 *   - conditions: embedded <ConditionBuilder isArrayInput ...> with the element
 *     class resolved from the selected source_field. Only visible in predicate mode.
 *   - registry model picker + register_kept checkbox: only visible in registry_dedup mode.
 *   - on_empty: continue / exit.
 */

interface FilterStepConfigProps {
  /** Current (possibly partial) filter config, as loaded from the step. */
  config: Partial<FilterConfig>;
  /** Callback invoked whenever the config changes. */
  onChange: (config: FilterConfig) => void;
  /** Upstream step whose output_model provides the source field options. */
  previousStep?: WorkflowStep | any;
  /** Workflow id (passed through to ConditionBuilder for nested-create flows). */
  workflowId?: number;
  /**
   * Project id used to fetch registry-enabled models (AC-1358). When absent,
   * the registry model picker is rendered but disabled.
   */
  projectId?: number;
}

const LIST_PREFIX = /^(?:list|List)\[(.+)\]$/;

// Property values come in two shapes across the codebase:
//   - flat string  : "list[RDAPDomainResult]"            (legacy InstructionAssistant)
//   - rich dict    : { type: "list[RDAPDomainResult]", description: "..." }
//                                                       (recommended-schema flow)
// Normalize both before testing the list[X] regex.
function extractElementClassName(typeAnnotation: string | any): string | null {
  let typeStr: string | null = null;
  if (typeof typeAnnotation === 'string') {
    typeStr = typeAnnotation;
  } else if (typeAnnotation && typeof typeAnnotation === 'object' && typeof typeAnnotation.type === 'string') {
    typeStr = typeAnnotation.type;
  }
  if (!typeStr) return null;
  const match = typeStr.match(LIST_PREFIX);
  if (!match) return null;
  const inner = match[1].trim();
  const scalar = new Set(['int', 'str', 'float', 'bool', 'Any']);
  if (scalar.has(inner)) return null;
  return inner;
}

function getListFields(previousStep: any): Record<string, string> {
  const props: Record<string, any> | undefined =
    previousStep?.agent?.output_model?.properties ||
    previousStep?.output_model?.properties;
  if (!props) return {};
  const out: Record<string, string> = {};
  for (const [field, type] of Object.entries(props)) {
    const cls = extractElementClassName(type);
    if (cls) out[field] = cls;
  }
  return out;
}

function conditionsToStrings(conditions: Condition[]): string[] {
  return conditions
    .filter((c) => c.field && c.operator !== undefined && c.value !== undefined && c.value !== '')
    .map((c) => {
      const value = typeof c.value === 'string' ? `"${c.value}"` : String(c.value);
      return `${c.field} ${c.operator} ${value}`;
    });
}

function stringsToConditions(strings: string[]): Condition[] {
  return (strings || []).map((s) => {
    const trimmed = (s || '').trim();
    const ops = ['>=', '<=', '!=', '==', '>', '<'];
    for (const op of ops) {
      const idx = trimmed.indexOf(` ${op} `);
      if (idx >= 0) {
        const field = trimmed.slice(0, idx).trim();
        const valueRaw = trimmed.slice(idx + op.length + 2).trim();
        let value: any = valueRaw;
        if ((valueRaw.startsWith('"') && valueRaw.endsWith('"')) ||
            (valueRaw.startsWith("'") && valueRaw.endsWith("'"))) {
          value = valueRaw.slice(1, -1);
        } else if (!isNaN(Number(valueRaw))) {
          value = Number(valueRaw);
        }
        return { field, operator: op, value };
      }
    }
    return { field: trimmed, operator: '==', value: '' };
  });
}

const FilterStepConfig: React.FC<FilterStepConfigProps> = ({
  config,
  onChange,
  previousStep,
  workflowId,
  projectId,
}) => {
  const sourceField = config.source_field || '';
  const outputField = config.output_field ?? sourceField;
  const conditionStrings = config.conditions || [];
  const onEmpty = config.on_empty || 'continue';

  // AC-1358: mode/registry-dedup state. We mirror these in local state so
  // the UI swap (predicate <-> registry_dedup) happens immediately on user
  // interaction, even before the parent has round-tripped the config back
  // into props. This matches the existing localConditions pattern (AC-1309).
  // We sync from props when they change externally (e.g. step reloaded).
  const propsMode: 'predicate' | 'registry_dedup' = config.mode || 'predicate';
  const propsRegistryModelId: number | null =
    config.registry_model_id === undefined ? null : config.registry_model_id;
  const propsRegisterKept: boolean = config.register_kept !== false;

  const [mode, setMode] = React.useState<'predicate' | 'registry_dedup'>(propsMode);
  const [registryModelId, setRegistryModelId] = React.useState<number | null>(
    propsRegistryModelId,
  );
  const [registerKept, setRegisterKept] = React.useState<boolean>(propsRegisterKept);

  React.useEffect(() => {
    setMode(propsMode);
  }, [propsMode]);
  React.useEffect(() => {
    setRegistryModelId(propsRegistryModelId);
  }, [propsRegistryModelId]);
  React.useEffect(() => {
    setRegisterKept(propsRegisterKept);
  }, [propsRegisterKept]);

  const listFields = React.useMemo(() => getListFields(previousStep), [previousStep]);
  const elementClassName =
    config.element_class_name ||
    (sourceField ? listFields[sourceField] : undefined) ||
    undefined;

  // AC-1309: Keep the object-form conditions in local state so in-progress
  // partial rows (e.g. the empty row produced by clicking "Add Condition")
  // survive the round-trip. The parent only sees the string-form list, and
  // conditionsToStrings filters incomplete rows out, so deriving
  // conditionObjects from the parent every render would erase the partial
  // row the user is mid-editing.
  //
  // We sync from props only when the incoming conditionStrings differ from
  // what our local state would emit — this catches external replacements
  // (step reloaded after save) without clobbering local edits.
  const [localConditions, setLocalConditions] = React.useState<Condition[]>(
    () => stringsToConditions(conditionStrings),
  );
  const lastEmittedRef = React.useRef<string>(JSON.stringify(conditionStrings));

  React.useEffect(() => {
    const incoming = JSON.stringify(conditionStrings);
    if (incoming !== lastEmittedRef.current) {
      lastEmittedRef.current = incoming;
      setLocalConditions(stringsToConditions(conditionStrings));
    }
  }, [conditionStrings]);

  // AC-1358: Load registry-enabled models on demand when the user is in
  // registry_dedup mode and we have a projectId. The list is small (one row
  // per registry-enabled model) so a simple fetch on entry is fine for v1.
  const [registryModels, setRegistryModels] = React.useState<RegistryEnabledModel[]>([]);
  const [registryModelsLoaded, setRegistryModelsLoaded] = React.useState(false);

  React.useEffect(() => {
    if (mode !== 'registry_dedup') return;
    if (projectId === undefined || projectId === null) return;
    let cancelled = false;
    (async () => {
      try {
        const models = await apiService.getRegistryEnabledModels(projectId as number);
        if (!cancelled) {
          setRegistryModels(models || []);
          setRegistryModelsLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setRegistryModels([]);
          setRegistryModelsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, projectId]);

  const emit = (next: Partial<FilterConfig>) => {
    // AC-1358: Preserve mode-related fields across emits so users can flip
    // between modes without losing their selections.
    const merged: FilterConfig = {
      source_field: next.source_field ?? sourceField,
      output_field: next.output_field ?? outputField ?? next.source_field ?? sourceField,
      conditions: next.conditions ?? conditionStrings,
      on_empty: next.on_empty ?? onEmpty,
      element_class_name: next.element_class_name ?? config.element_class_name,
      mode: next.mode ?? mode,
      registry_model_id:
        next.registry_model_id !== undefined ? next.registry_model_id : registryModelId,
      register_kept:
        next.register_kept !== undefined ? next.register_kept : registerKept,
    };
    onChange(merged);
  };

  const handleSourceFieldChange = (value: string) => {
    emit({
      source_field: value,
      // Default output_field to match source_field on first selection.
      output_field: outputField && outputField !== sourceField ? outputField : value,
      element_class_name: listFields[value] || undefined,
    });
  };

  const handleOutputFieldChange = (value: string) => {
    emit({ output_field: value });
  };

  const handleOnEmptyChange = (value: 'continue' | 'exit') => {
    emit({ on_empty: value });
  };

  const handleConditionsChange = (nextConditions: Condition[]) => {
    // AC-1309: update local state first so partial rows persist; then emit
    // the filtered string form to the parent. The ref records what we just
    // emitted so the useEffect above can distinguish external prop changes
    // from our own writes and avoid clobbering the partial rows.
    setLocalConditions(nextConditions);
    const nextStrings = conditionsToStrings(nextConditions);
    lastEmittedRef.current = JSON.stringify(nextStrings);
    emit({ conditions: nextStrings });
  };

  const handleModeChange = (value: 'predicate' | 'registry_dedup') => {
    if (value === mode) return;
    setMode(value);
    if (value === 'registry_dedup') {
      // AC-1358 / AC-1356: backend rejects predicate-mode `conditions` when
      // mode='registry_dedup'. Clear them so the persisted config validates.
      // Also clear the local object-form so the ConditionBuilder doesn't
      // ghost-render rows on next mode flip.
      setLocalConditions([]);
      lastEmittedRef.current = JSON.stringify([]);
      emit({ mode: 'registry_dedup', conditions: [] });
    } else {
      emit({ mode: 'predicate' });
    }
  };

  const handleRegistryModelChange = (rawValue: string) => {
    // Empty string => "Auto from upstream agent" (registry_model_id null).
    const next: number | null = rawValue === '' ? null : Number(rawValue);
    setRegistryModelId(next);
    emit({ registry_model_id: next });
  };

  const handleRegisterKeptChange = (checked: boolean) => {
    setRegisterKept(checked);
    emit({ register_kept: checked });
  };

  const projectIdMissing = projectId === undefined || projectId === null;
  const registryPickerValue = registryModelId === null ? '' : String(registryModelId);

  // AC-1370: Resolve the registry model the backend will actually use, so we
  // can show the dedup fields up front. The resolution rules mirror
  // FilterStepExecutor._resolve_registry_model:
  //   1. registry_model_id explicit -> use that model
  //   2. else "auto from upstream" -> match by class name on the source field
  // If we can't resolve it (no registry-enabled model with that class name),
  // we surface a hint instead of silently letting the run fail at execute time.
  const resolvedRegistryModel: RegistryEnabledModel | null = React.useMemo(() => {
    if (mode !== 'registry_dedup') return null;
    if (registryModelId !== null) {
      return registryModels.find((m) => m.model_id === registryModelId) || null;
    }
    if (elementClassName) {
      return registryModels.find((m) => m.class_name === elementClassName) || null;
    }
    return null;
  }, [mode, registryModelId, registryModels, elementClassName]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-medium text-rose-700 dark:text-rose-300">
        <Filter className="w-4 h-4" />
        <span>Filter step configuration</span>
      </div>

      {/* AC-1358: Mode selector. predicate (default) keeps the existing
          ConditionBuilder UI; registry_dedup swaps in the registry model
          picker + register_kept checkbox. */}
      <div>
        <label htmlFor="filter-mode" className="block text-sm font-medium mb-1">
          Mode
        </label>
        <select
          id="filter-mode"
          aria-label="mode"
          className="input w-full"
          value={mode}
          onChange={(e) =>
            handleModeChange(e.target.value as 'predicate' | 'registry_dedup')
          }
        >
          <option value="predicate">Predicate (filter by conditions)</option>
          <option value="registry_dedup">Registry dedup (skip already-seen items)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Predicate keeps elements matching ALL conditions. Registry dedup
          drops elements already recorded in the chosen model registry.
        </p>
      </div>

      <div>
        <label htmlFor="filter-source-field" className="block text-sm font-medium mb-1">
          Source field
          <span className="text-red-600 ml-1">*</span>
        </label>
        {/* Native select keeps the option list in the DOM (accessibility +
            deterministic testability). */}
        <select
          id="filter-source-field"
          aria-label="source field"
          className="input w-full"
          value={sourceField}
          onChange={(e) => handleSourceFieldChange(e.target.value)}
        >
          <option value="">Select the list field to filter...</option>
          {Object.entries(listFields).map(([f, cls]) => (
            <option key={f} value={f}>
              {f} (list[{cls}])
            </option>
          ))}
        </select>
        {Object.keys(listFields).length === 0 && (
          <p className="text-xs text-gray-500 mt-1">
            No list[X] fields found on the upstream step's output model.
          </p>
        )}
      </div>

      <div>
        <label htmlFor="filter-output-field" className="block text-sm font-medium mb-1">
          Output field
        </label>
        <input
          id="filter-output-field"
          type="text"
          className="input w-full"
          placeholder="Defaults to source field"
          value={outputField}
          onChange={(e) => handleOutputFieldChange(e.target.value)}
        />
        <p className="text-xs text-gray-500 mt-1">
          Name under which the filtered list is emitted. Defaults to the source field.
        </p>
      </div>

      {mode === 'predicate' && (
        <div>
          <label className="block text-sm font-medium mb-1">Conditions</label>
          <ConditionBuilder
            conditions={localConditions}
            onChange={handleConditionsChange}
            previousStep={previousStep}
            workflowId={workflowId}
            isArrayInput={true}
            elementContextClassName={elementClassName}
            sourceFieldPath={sourceField || undefined}
          />
          <p className="text-xs text-gray-500 mt-1">
            Conditions are evaluated per element. An element is kept when ALL conditions pass.
          </p>
        </div>
      )}

      {mode === 'registry_dedup' && (
        <>
          <div>
            <label htmlFor="filter-registry-model" className="block text-sm font-medium mb-1">
              Registry model
            </label>
            <select
              id="filter-registry-model"
              aria-label="registry model"
              className="input w-full"
              value={registryPickerValue}
              disabled={projectIdMissing}
              onChange={(e) => handleRegistryModelChange(e.target.value)}
            >
              <option value="">Auto from upstream agent</option>
              {registryModels.map((m) => (
                <option key={m.model_id} value={String(m.model_id)}>
                  {m.class_name} ({m.entry_count} entries)
                </option>
              ))}
            </select>
            {projectIdMissing && (
              <p className="text-xs text-gray-500 mt-1">
                Project required to load registry-enabled models. Select a project first.
              </p>
            )}
            {!projectIdMissing && registryModelsLoaded && registryModels.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                No registry-enabled models in this project. Enable registry on a model first.
              </p>
            )}
            {!projectIdMissing && registryModels.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                "Auto from upstream agent" infers the model from the upstream
                step's output type. Pick a specific model to override.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="filter-register-kept" className="flex items-center gap-2 text-sm font-medium">
              <input
                id="filter-register-kept"
                type="checkbox"
                aria-label="register kept"
                checked={registerKept}
                onChange={(e) => handleRegisterKeptChange(e.target.checked)}
              />
              Register kept
            </label>
            <p className="text-xs text-gray-500 mt-1">
              When enabled, kept items are written to the registry so future
              runs treat them as duplicates. Disable for check-only behavior.
            </p>
          </div>

          {/* AC-1370: Surface the dedup fields the backend will actually use.
              These come from the registry model's schema config, not from
              this filter step - users were silently getting wrong-or-empty
              dedup keys when the schema's registry_dedup_fields weren't set
              the way they assumed. */}
          {registryModelsLoaded && (
            <div
              data-testid="resolved-dedup-summary"
              className="rounded border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-3"
            >
              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Will dedup on
              </div>
              {!resolvedRegistryModel && registryModelId === null && !elementClassName && (
                <p className="text-xs text-gray-500">
                  Pick a Source field above (or a Registry model) so we can
                  show which fields will be used for dedup.
                </p>
              )}
              {!resolvedRegistryModel && registryModelId === null && elementClassName && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  No registry-enabled model named <code className="font-mono">{elementClassName}</code> in this project. Enable registry on that schema, or pick a specific model above.
                </p>
              )}
              {!resolvedRegistryModel && registryModelId !== null && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Selected registry model is not in this project's registry-enabled list.
                </p>
              )}
              {resolvedRegistryModel && (
                <>
                  {(resolvedRegistryModel.registry_dedup_fields || []).length > 0 ? (
                    <p className="text-sm">
                      {(resolvedRegistryModel.registry_dedup_fields || []).map((f, i) => (
                        <React.Fragment key={f}>
                          {i > 0 && <span className="text-gray-500">, </span>}
                          <code className="font-mono text-primary-700 dark:text-primary-400">
                            {resolvedRegistryModel.class_name}.{f}
                          </code>
                        </React.Fragment>
                      ))}
                    </p>
                  ) : (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      <code className="font-mono">{resolvedRegistryModel.class_name}</code> has no
                      registry dedup fields configured - the entire object will be used as the dedup key (rarely what you want for list dedup).
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Dedup fields are configured on the schema's "Registry dedup fields" setting, not on this filter step.
                  </p>
                </>
              )}
            </div>
          )}
        </>
      )}

      <div>
        <span className="block text-sm font-medium mb-1">On empty result</span>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="filter-on-empty"
              value="continue"
              checked={onEmpty === 'continue'}
              onChange={() => handleOnEmptyChange('continue')}
            />
            continue
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="filter-on-empty"
              value="exit"
              checked={onEmpty === 'exit'}
              onChange={() => handleOnEmptyChange('exit')}
            />
            exit
          </label>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          When zero elements pass the predicate: continue the run with an empty list,
          or exit the orchestration.
        </p>
      </div>

    </div>
  );
};

export default FilterStepConfig;
